import { Component, inject, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { LicenseService } from '../../services/license.service';
import { LogService } from '../../services/log.service';
import { ProductService } from '../../services/product.service';

// Importaciones de interfaces.
import { ILicense } from '../../interfaces/license.interface';
import { IProduct } from '../../interfaces/product.interface';
import { IProductResponse } from '../../interfaces/product-response.interface';

// Importaciones de modelos.
import { License } from '../../models/license.model';
import { Log } from '../../models/log.model';
import { Product } from '../../models/product.model';

/**
 * Componente de diálogo para la creación de la licencia.
 *
 * Este componente proporciona una interfaz para crear una licencia.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos.
 */
@Component({
  selector: 'app-license-create-dialog',
  templateUrl: './license-create-dialog.component.html',
  standalone: false,
})
export class LicenseCreateDialogComponent implements OnInit {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de la creación de la licencia.
   */
  private readonly dialogRef: MatDialogRef<LicenseCreateDialogComponent> =
    inject(MatDialogRef<LicenseCreateDialogComponent>);

  /**
   * Indica si la carga de los productos está en curso.
   *
   * Se utiliza para mostrar un indicador de carga mientras se obtienen los productos disponibles.
   */
  loadingProducts: boolean = false;

  /**
   * Lista de productos disponibles.
   *
   * Contiene los productos que pueden asignarse a la licencia en el proceso de creación.
   */
  products: IProduct[] = [];

  /**
   * Indica si la operación de creado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se crea la licencia.
   */
  loadingCreate: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización de la licencia.
   *
   * Gestiona los campos y validaciones necesarias para actualizar la información de la licencia.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    productId: new FormControl('', Validators.required),
    alias: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(3),
    ]),
    name: new FormControl('', Validators.required),
    type: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(2),
    ]),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'productId'.
   *
   * Retorna el control asociado a la propiedad 'productId' del formulario,
   * que gestiona el producto de la licencia.
   *
   * @returns El control de formulario para 'productId'.
   */
  get formProductId(): AbstractControl | null {
    return this.form.get('productId');
  }

  /**
   * Obtiene el control del formulario para el campo 'alias'.
   *
   * Retorna el control asociado a la propiedad 'alias' del formulario,
   * que gestiona el alias de la licencia.
   *
   * @returns El control de formulario para 'alias'.
   */
  get formAlias(): AbstractControl | null {
    return this.form.get('alias');
  }

  /**
   * Obtiene el control del formulario para el campo 'name'.
   *
   * Retorna el control asociado a la propiedad 'name' del formulario,
   * que gestiona el nombre de la licencia.
   *
   * @returns El control de formulario para 'name'.
   */
  get formName(): AbstractControl | null {
    return this.form.get('name');
  }

  /**
   * Obtiene el control del formulario para el campo 'type'.
   *
   * Retorna el control asociado a la propiedad 'type' del formulario,
   * que gestiona el tipo de la licencia.
   *
   * @returns El control de formulario para 'type'.
   */
  get formType(): AbstractControl | null {
    return this.form.get('type');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param licenseService - Servicio para gestionar la información de las licencias.
   * @param logService - Servicio para gestionar los registros.
   * @param productService - Servicio para gestionar los productos de las licencias.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private licenseService: LicenseService,
    private logService: LogService,
    private productService: ProductService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta cuando el componente es inicializado. Es utilizado para realizar tareas
   * de configuración o cargar datos necesarios para la operación del componente.
   */
  async ngOnInit(): Promise<void> {
    this.loadingProducts = true;

    try {
      const productResponse: IProductResponse =
        await this.productService.getAll();
      this.products = productResponse.products.map((product: IProduct) => {
        return new Product(product);
      });
    } finally {
      this.loadingProducts = false;
    }
  }

  /**
   * Crear la licencia.
   *
   * Este método se encarga de realizar el proceso de creado de la licencia.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async create(): Promise<void> {
    this.loadingCreate = true;
    this.errors = [];

    try {
      const licenseResponse: ILicense = await this.licenseService.create(
        new License({
          product: new Product({ id: Number(this.formProductId?.value) }),
          alias: this.formAlias?.value,
          name: this.formName?.value,
          type: this.formType?.value,
          disabled: Number(!this.formStatus?.value),
        })
      );

      // Añadir un registro de creación de la licencia.
      try {
        await this.logService.create(
          new Log({
            description: `Ha creado la licencia '${licenseResponse.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de creación de la licencia.'
          );
        }
      }

      this.snackBar.open(
        `Has creado correctamente la licencia ${licenseResponse.id}`,
        '',
        {
          duration: 5000,
        }
      );

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loadingCreate = false;
  }
}
